// ConfettiOverlay.jsx – Simple pastel confetti particle animation.
// Renders via a fixed-position overlay, auto-hides after duration.
import React, { useEffect, useState, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import ReactDOM from 'react-dom';

const PASTEL_COLORS = [
  '#ffd1dc', '#b5ead7', '#c7ceea', '#ffdac1', '#e2f0cb',
  '#fff1c1', '#d4a5a5', '#b0e0e6', '#ffe4e1', '#dcedc1',
];

const PARTICLE_COUNT = 50;

function randomBetween(a, b) {
  return a + Math.random() * (b - a);
}

function createParticle(width, height) {
  return {
    x: randomBetween(0, width),
    y: randomBetween(-height * 0.3, -20),
    vx: randomBetween(-2, 2),
    vy: randomBetween(2, 6),
    size: randomBetween(6, 14),
    color: PASTEL_COLORS[Math.floor(Math.random() * PASTEL_COLORS.length)],
    rotation: randomBetween(0, 360),
    rotationSpeed: randomBetween(-8, 8),
    shape: Math.random() > 0.5 ? 'circle' : 'rect',
    opacity: 1,
  };
}

/**
 * @param {{ active: boolean, duration?: number, onDone?: () => void }} props
 */
const ConfettiOverlay = ({ active, duration = 800, onDone }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const particlesRef = useRef([]);
  const startRef = useRef(0);
  const activeRef = useRef(active);
  const durationRef = useRef(duration);
  const onDoneRef = useRef(onDone);
  const [visible, setVisible] = useState(false);

  // Keep refs in sync without triggering effect re-runs
  useEffect(() => { activeRef.current = active; }, [active]);
  useEffect(() => { durationRef.current = duration; }, [duration]);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

  // Stable animate function — reads everything from refs
  const animate = useCallback((timestamp) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dur = durationRef.current;
    const elapsed = timestamp - startRef.current;
    const fadeStart = dur * 0.6;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    particlesRef.current.forEach((p) => {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.15; // gravity
      p.rotation += p.rotationSpeed;

      // Fade out towards end
      if (elapsed > fadeStart) {
        p.opacity = Math.max(0, 1 - (elapsed - fadeStart) / (dur - fadeStart));
      }

      ctx.save();
      ctx.globalAlpha = p.opacity;
      ctx.translate(p.x, p.y);
      ctx.rotate((p.rotation * Math.PI) / 180);
      ctx.fillStyle = p.color;

      if (p.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      }
      ctx.restore();
    });

    if (elapsed < dur) {
      animRef.current = requestAnimationFrame(animate);
    } else if (activeRef.current) {
      // Still active → respawn particles for continuous confetti
      const w = canvas.width || window.innerWidth;
      const h = canvas.height || window.innerHeight;
      particlesRef.current = Array.from({ length: PARTICLE_COUNT }, () => createParticle(w, h));
      startRef.current = performance.now();
      animRef.current = requestAnimationFrame(animate);
    } else {
      setVisible(false);
      onDoneRef.current?.();
    }
  }, []); // stable — no deps

  // Start / stop based on active prop
  useEffect(() => {
    if (!active) {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      setVisible(false);
      return;
    }

    // Activate: set visible first, then start animation after canvas mounts
    setVisible(true);
  }, [active]);

  // Once visible + active, set up canvas and start animation
  useEffect(() => {
    if (!visible || !active) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = w;
    canvas.height = h;
    particlesRef.current = Array.from({ length: PARTICLE_COUNT }, () => createParticle(w, h));
    startRef.current = performance.now();
    animRef.current = requestAnimationFrame(animate);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [visible, active, animate]);

  if (!visible) return null;

  const overlay = (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%', display: 'block' }}
      />
    </Box>
  );

  // Render via portal to ensure it covers everything
  return ReactDOM.createPortal(overlay, document.body);
};

export default ConfettiOverlay;
